import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Switch } from "../../components/ui/switch";
import { Checkbox } from "../../components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { api } from "../client";
import { PageHeader, Panel, Pill, Field, Empty } from "../ui";
import AnchorVenueModal from "./AnchorVenueModal";
import PartnerApplications from "./PartnerApplications";
import { Tabs, TabsList, TabsTrigger } from "../../components/ui/tabs";

const CATS = ["Coffee", "Brewery & Taphouse", "Brewery & Pub", "Sports Bar", "Restaurant", "Nightlife", "Wine & Cocktails", "Pizza & Pub", "Activity"];
const BLANK = { name: "", category: "Coffee", address: "", city: "", state: "OR", latitude: "", longitude: "", perk_description: "", is_anchor_partner: false, contact_name: "", contact_email: "", contact_phone: "" };

const VenueModal = ({ venue, onClose, onSaved }) => {
  const [f, setF] = useState(BLANK);
  const [busy, setBusy] = useState(false);
  useEffect(() => { setF(venue?.id ? { ...BLANK, ...venue, latitude: venue.latitude ?? "", longitude: venue.longitude ?? "", perk_description: venue.perk_description || "", contact_name: venue.contact_name || "", contact_email: venue.contact_email || "", contact_phone: venue.contact_phone || "" } : BLANK); }, [venue]);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    const num = (v) => (v === "" || v === null ? null : Number(v));
    const body = { name: f.name, category: f.category, address: f.address, city: f.city, state: f.state, perk_description: f.perk_description || null, is_anchor_partner: f.is_anchor_partner, latitude: num(f.latitude), longitude: num(f.longitude), contact_name: f.contact_name || null, contact_email: f.contact_email || null, contact_phone: f.contact_phone || null };
    try {
      await api(venue.id ? `/staff/venues/${venue.id}` : "/staff/venues", { method: venue.id ? "PUT" : "POST", body });
      toast.success(venue.id ? "Venue updated" : "Venue added"); onSaved();
    } catch (e2) { toast.error(e2.message); } finally { setBusy(false); }
  };
  return (
    <Dialog open={!!venue} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="staff-root max-h-[90vh] overflow-y-auto border-[var(--s-line)] bg-[var(--s-panel)]" data-testid="venue-modal">
        <DialogHeader><DialogTitle className="s-display">{venue?.id ? "Edit venue" : "Add new venue"}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <Field label="Name"><Input required value={f.name} onChange={set("name")} className="s-input" data-testid="venue-name" /></Field>
          <Field label="Category">
            <Input required list="venue-category-options" value={f.category} onChange={set("category")} className="s-input" data-testid="venue-category" />
            <datalist id="venue-category-options">{CATS.map((c) => <option key={c} value={c} />)}</datalist>
          </Field>
          <Field label="Full street address"><Input required value={f.address} onChange={set("address")} className="s-input" data-testid="venue-address" /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="City"><Input required value={f.city} onChange={set("city")} className="s-input" data-testid="venue-city" /></Field>
            <Field label="State"><Input required value={f.state} onChange={set("state")} className="s-input" data-testid="venue-state" /></Field>
            <Field label="Latitude (optional)"><Input type="number" step="any" value={f.latitude} onChange={set("latitude")} className="s-input" data-testid="venue-lat" /></Field>
            <Field label="Longitude (optional)"><Input type="number" step="any" value={f.longitude} onChange={set("longitude")} className="s-input" data-testid="venue-lng" /></Field>
          </div>
          <Field label="Perk description"><Input value={f.perk_description} onChange={set("perk_description")} placeholder="10% off drinks for Sparkd members" className="s-input" data-testid="venue-perk" /></Field>
          <Field label="Contact name"><Input value={f.contact_name} onChange={set("contact_name")} className="s-input" data-testid="venue-contact-name" /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Contact email"><Input type="email" value={f.contact_email} onChange={set("contact_email")} className="s-input" data-testid="venue-contact-email" /></Field>
            <Field label="Contact phone"><Input type="tel" value={f.contact_phone} onChange={set("contact_phone")} className="s-input" data-testid="venue-contact-phone" /></Field>
          </div>
          <label className="flex items-center gap-2 text-sm"><Checkbox checked={f.is_anchor_partner} onCheckedChange={(v) => setF({ ...f, is_anchor_partner: !!v })} data-testid="venue-anchor" />Anchor partner</label>
          <Button type="submit" disabled={busy} className="s-btn w-full" data-testid="venue-submit">{busy ? "Saving…" : venue?.id ? "Save changes" : "Add venue"}</Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default function Venues() {
  const [rows, setRows] = useState(null);
  const [editing, setEditing] = useState(null);
  const [anchor, setAnchor] = useState(false);
  const [tab, setTab] = useState("venues");
  const [appCount, setAppCount] = useState(null);
  const load = useCallback(() => { api("/staff/venues").then(setRows).catch((e) => toast.error(e.message)); }, []);
  useEffect(() => { load(); }, [load]);
  const toggle = async (v) => {
    try { await api(`/staff/venues/${v.id}/active`, { method: "POST", body: { is_active: !v.is_active } }); toast.success(v.is_active ? "Venue deactivated" : "Venue activated"); load(); }
    catch (e) { toast.error(e.message); }
  };
  return (
    <div>
      <PageHeader kicker="Venues & partners" title="Venue management">
        <div className="flex flex-wrap gap-2">
          <Button className="s-btn" onClick={() => setAnchor(true)} data-testid="anchor-add-btn"><Plus size={16} className="mr-2" />Add new anchor venue</Button>
          <Button variant="outline" className="s-ghost" onClick={() => setEditing({})} data-testid="venue-add-btn"><Plus size={16} className="mr-2" />Add new venue</Button>
        </div>
      </PageHeader>
      <Tabs value={tab} onValueChange={setTab} className="mb-6"><TabsList>
        <TabsTrigger value="venues" data-testid="venues-tab-venues">Venues</TabsTrigger>
        <TabsTrigger value="apps" data-testid="venues-tab-apps">Partner applications{appCount ? <span className="ml-2 rounded-full bg-[var(--s-accent)] px-2 text-[10px] text-black" data-testid="partner-apps-badge">{appCount}</span> : null}</TabsTrigger>
      </TabsList></Tabs>
      <div className={tab === "apps" ? "" : "hidden"}><PartnerApplications onCount={setAppCount} onPublished={load} /></div>
      <Panel className={`overflow-x-auto ${tab === "venues" ? "" : "hidden"}`}>
        {rows === null ? <p className="p-6 text-sm text-[var(--s-dim)]">Loading…</p> : rows.length === 0 ? <Empty>No venues yet.</Empty> : (
          <table className="w-full text-sm" data-testid="venues-table">
            <thead className="s-mono text-left text-[11px] uppercase tracking-wider text-[var(--s-dim)]">
              <tr>{["Venue", "Category", "City", "Address", "Perk", "Contact", "Anchor", "Active", ""].map((h) => <th key={h} className="px-5 py-3 font-normal">{h}</th>)}</tr>
            </thead>
            <tbody className="divide-y divide-[var(--s-line)]">
              {rows.map((v) => (
                <tr key={v.id} data-testid={`venue-row-${v.id}`} className={`transition-colors hover:bg-white/[0.02] ${v.is_active ? "" : "opacity-50"}`}>
                  <td className="px-5 py-4 font-medium">{v.name}</td>
                  <td className="px-5 py-4"><Pill>{v.category}</Pill></td>
                  <td className="px-5 py-4">{v.city}, {v.state}</td>
                  <td className="px-5 py-4 text-[var(--s-dim)]">{v.address}</td>
                  <td className="max-w-xs px-5 py-4 text-[var(--s-dim)]">{v.perk_description || "—"}</td>
                  <td className="px-5 py-4" data-testid={`venue-contact-${v.id}`}>
                    {v.contact_name || v.contact_email || v.contact_phone ? <>
                      <p>{v.contact_name || "—"}</p>
                      {v.contact_email && <a href={`mailto:${v.contact_email}`} className="block text-xs text-[var(--s-accent)] hover:underline">{v.contact_email}</a>}
                      {v.contact_phone && <a href={`tel:${v.contact_phone}`} className="block text-xs text-[var(--s-accent)] hover:underline">{v.contact_phone}</a>}
                    </> : <span className="text-[var(--s-dim)]">—</span>}
                  </td>
                  <td className="px-5 py-4">{v.is_anchor_partner ? <Pill tone="warn">Anchor</Pill> : <Pill>Standard</Pill>}{v.is_partner && <Pill tone="ok">Partner</Pill>}</td>
                  <td className="px-5 py-4"><Switch checked={v.is_active} onCheckedChange={() => toggle(v)} data-testid={`venue-active-${v.id}`} /></td>
                  <td className="px-5 py-4 text-right"><Button size="sm" variant="outline" className="s-ghost" onClick={() => setEditing(v)} data-testid={`venue-edit-${v.id}`}>Edit</Button></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Panel>
      <VenueModal venue={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />
      <AnchorVenueModal open={anchor} onClose={() => setAnchor(false)} onSaved={load} />
    </div>
  );
}
